import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { Avatar } from "../components/AppIcon";
import { Icon, Logo } from "../components/Icon";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

function Wordmark() {
  return (
    <Link to="/" className="wordmark" aria-label="Omni home" style={{ color: "inherit", textDecoration: "none", alignSelf: "flex-start" }}>
      <Logo size={30} />
      omni
    </Link>
  );
}

/** The code from an invite: typed, or pasted as the whole link (…/join/<code>). */
export function inviteCodeFrom(text: string): string {
  const t = text.trim();
  const m = /\/join\/([^/?#\s]+)/.exec(t);
  return (m ? m[1] : t).trim();
}

function InviteCodeForm({ autoFocus }: { autoFocus?: boolean }) {
  const nav = useNavigate();
  const [value, setValue] = useState("");
  return (
    <form className="row" style={{ width: "100%" }} onSubmit={(e) => { e.preventDefault(); const c = inviteCodeFrom(value); if (c) nav(`/join/${encodeURIComponent(c)}`); }}>
      <input className="input" autoFocus={autoFocus} placeholder="Invite code or link" aria-label="Invite code or link" autoCapitalize="off" autoCorrect="off" value={value} onChange={(e) => setValue(e.target.value)} />
      <button className="btn" disabled={!inviteCodeFrom(value)}>Continue</button>
    </form>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState(() => new URLSearchParams(loc.search).get("email") ?? "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      nav((loc.state as { from?: string } | null)?.from ?? "/", { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <Wordmark />
      <div className="stack" style={{ marginTop: 12 }}>
        <h1>One sign-in for everything you run.</h1>
        <p className="lede">Money, calendars, documents. Each gets its own app, and they all live in one space.</p>
      </div>
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span className="field-label">Email</span>
          <input className="input" type="email" autoComplete="email" placeholder="you@example.com" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Password</span>
          <input className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {error && <p className="error small" role="alert">{error}</p>}
        <button className="btn btn-primary btn-block" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <div className="divider-or">or</div>
      <Link className="btn btn-block" to="/register">
        <Icon name="profile" size={20} />Create an account
      </Link>
      <div className="stack center-items small" style={{ marginTop: "auto", gap: 8 }}>
        {code === null ? (
          <div>
            Got an invite? <button className="link-btn" style={{ minHeight: 0, display: "inline" }} onClick={() => setCode("")}>Enter your code</button>
          </div>
        ) : (
          <InviteCodeForm autoFocus />
        )}
        <Link to="/settings/server" className="muted xs">Server settings</Link>
      </div>
    </div>
  );
}

interface InviteLookup {
  valid: boolean;
  email?: string | null;
  note?: string;
  inviter?: { display_name: string; handle: string; avatar_url: string };
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const { code } = useParams();
  const [mode, setMode] = useState<"open" | "invite" | null>(null);
  const [invite, setInvite] = useState<InviteLookup | null>(null);
  const [form, setForm] = useState({ display_name: "", email: new URLSearchParams(location.search).get("email") ?? "", password: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [askCode, setAskCode] = useState(false);

  useEffect(() => {
    api<{ registration_mode: "open" | "invite" }>("/api/auth/config", { auth: false })
      .then((c) => setMode(c.registration_mode))
      .catch(() => setMode("open"));
    if (code)
      api<InviteLookup>(`/api/invites/lookup/${code}`, { auth: false }).then((i) => {
        setInvite(i);
        if (i.email) setForm((f) => ({ ...f, email: i.email! }));
      });
  }, [code]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register({ ...form, invite_code: invite?.valid ? code : undefined });
      nav("/", { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const blocked = mode === "invite" && !invite?.valid;
  return (
    <div className="auth">
      <Wordmark />
      <h1 style={{ fontSize: 30 }}>Create your space</h1>
      {invite?.valid && invite.inviter && (
        <div className="card row">
          <Avatar name={invite.inviter.display_name} url={invite.inviter.avatar_url} />
          <div className="grow">
            <strong>{invite.inviter.display_name}</strong> invited you
            {invite.note && <span className="sub">“{invite.note}”</span>}
          </div>
        </div>
      )}
      {code && invite && !invite.valid && <p className="error small">This invite link is invalid or has expired.</p>}
      {blocked ? (
        <div className="card stack">
          <p className="muted" style={{ margin: 0 }}>This Omni server is invite-only. Enter the code from your invite, or paste the whole invite link.</p>
          <InviteCodeForm />
          <p className="xs muted" style={{ margin: 0 }}>No invite yet? Ask someone who uses Omni to send you one from People › Invite.</p>
        </div>
      ) : (
        <form className="stack" onSubmit={submit}>
          <label className="field">
            <span className="field-label">Your name</span>
            <input className="input" required autoComplete="name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Email</span>
            <input className="input" type="email" required autoComplete="email" value={form.email} readOnly={!!invite?.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Password</span>
            {/* ADR-0007: 8+ characters, no composition rules */}
            <input className="input" type="password" required minLength={8} maxLength={128} autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <span className="xs muted">8 or more characters</span>
          </label>
          {error && <p className="error small" role="alert">{error}</p>}
          <button className="btn btn-primary btn-block" disabled={busy}>{busy ? "Creating…" : "Create account"}</button>
        </form>
      )}
      <div className="stack center-items small" style={{ marginTop: "auto", gap: 8 }}>
        {!blocked && !invite?.valid && mode !== null && (askCode ? <InviteCodeForm autoFocus /> : (
          <div className="muted">Got an invite? <button className="link-btn" style={{ minHeight: 0, display: "inline" }} onClick={() => setAskCode(true)}>Enter your code</button></div>
        ))}
        <div className="muted">Already have an account? <Link to="/login">Sign in</Link></div>
        <Link to="/settings/server" className="muted xs">Server settings</Link>
      </div>
    </div>
  );
}
